import React, { useState } from 'react';
import { type CurrentPolicyContext, CalculationMethod, type PolicyParameters } from '../../types';
import { CURRENCY_SYMBOLS } from '../../constants';

interface PolicyScheduleProps {
  context: CurrentPolicyContext;
  onUpdateParams: (params: PolicyParameters) => void;
  onFinishQuote: () => void;
  onNewQuote: () => void;
  isCalculating: boolean;
}

export const PolicySchedule: React.FC<PolicyScheduleProps> = ({
  context,
  onUpdateParams,
  onFinishQuote,
  onNewQuote,
  isCalculating,
}) => {
  const [viewMonthly, setViewMonthly] = useState(false);

  const currency = CURRENCY_SYMBOLS[context.policy_parameters.currency];
  const divFactor = viewMonthly ? 12 : 1;
  const vatRate = context.policy_parameters.vatEnabled ? 0.15 : 0;
  const brokeragePct = context.policy_parameters.brokeragePercent || 0;

  const fmt = (val: number) =>
    (val / divFactor).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const fmtWageroll = (val: number) =>
    val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const handleBrokerageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = parseFloat(e.target.value);
    if (val > 20) {
      val = 20;
    }
    if (val < 0) val = 0;
    onUpdateParams({ ...context.policy_parameters, brokeragePercent: val });
  };

  return (
    <div className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden flex flex-col font-sans">
      {/* Header Panel (Solid Indigo for distinction) */}
      <div className="bg-hr-indigo p-6 text-white relative overflow-hidden">
        {/* Subtle pattern for texture */}
        <div className="absolute top-[-50%] right-[-10%] w-48 h-48 bg-white/5 rounded-full blur-3xl"></div>

        <div className="relative z-10">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-base font-bold tracking-tight">Policy Ledger</h2>
            <div className="flex bg-indigo-900/50 rounded p-1 border border-white/10">
              <button
                type="button"
                onClick={() => setViewMonthly(false)}
                className={`px-3 py-1 text-micro font-bold uppercase tracking-wide rounded transition-all ${!viewMonthly ? 'bg-white text-hr-indigo shadow-sm' : 'text-white/50 hover:text-white'}`}
              >
                Annual
              </button>
              <button
                type="button"
                onClick={() => setViewMonthly(true)}
                className={`px-3 py-1 text-micro font-bold uppercase tracking-wide rounded transition-all ${viewMonthly ? 'bg-white text-hr-indigo shadow-sm' : 'text-white/50 hover:text-white'}`}
              >
                Monthly
              </button>
            </div>
          </div>
          <div>
            <p className="text-micro text-white/60 font-bold uppercase tracking-widest mb-1">
              Total Gross Premium
            </p>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-medium text-white/40">{currency}</span>
              <span className="text-metric font-extrabold tracking-tight text-white">
                {fmt(context.pricing_summary.grossPremium)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Table Panel (White body) - set to flex-1 to fill the remaining space of the fixed container */}
      <div className="flex-1 bg-white overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="px-4 py-3 text-micro font-bold text-slate-400 uppercase tracking-wide">
                Category
              </th>
              <th className="px-4 py-3 text-micro font-bold text-slate-400 uppercase tracking-wide text-right">
                Lives
              </th>
              <th className="px-4 py-3 text-micro font-bold text-slate-400 uppercase tracking-wide text-right">
                Exposure
              </th>
              <th className="px-4 py-3 text-micro font-bold text-slate-400 uppercase tracking-wide text-right">
                Net
              </th>
              <th className="px-4 py-3 text-micro font-bold text-slate-400 uppercase tracking-wide text-right">
                Gross (Incl)
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50 text-xs">
            {context.categories.map((cat) => {
              // Sum of risk premium for this category
              const net = cat.benefits.reduce((s, b) => s + b.netPremium, 0);

              // Approx Gross for display: Net / (1-Comm) * (1+VAT)
              // This helps the row totals look roughly aligned with the final gross,
              // though it may differ slightly due to Min Premium or Volume Discounts applied at Policy Level.
              const safeComm = Math.min(brokeragePct, 99) / 100;
              const grossExVat = net / (1 - safeComm);
              const gross = grossExVat * (1 + vatRate);

              return (
                <tr key={cat.id} className="hover:bg-indigo-50/30 transition-colors">
                  <td className="px-4 py-3 font-semibold text-slate-700">{cat.name}</td>
                  <td className="px-4 py-3 text-right text-slate-600 font-medium">
                    {cat.headcount}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-500 text-micro">
                    {cat.method === CalculationMethod.Stated ? fmtWageroll(cat.wageroll) : '-'}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600 font-medium">{fmt(net)}</td>
                  <td className="px-4 py-3 text-right font-bold text-hr-indigo">{fmt(gross)}</td>
                </tr>
              );
            })}
            {context.categories.length === 0 && (
              <tr>
                <td colSpan={5} className="p-8 text-center text-slate-400 italic text-micro">
                  No categories defined yet.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="bg-slate-50 border-t border-slate-200">
            <tr>
              <td
                colSpan={3}
                className="px-4 py-3 text-right font-bold text-slate-500 text-micro uppercase tracking-wide"
              >
                Premium Totals
              </td>
              <td className="px-4 py-3 text-right font-bold text-slate-700">
                {fmt(context.pricing_summary.netPremium)}
              </td>
              <td className="px-4 py-3 text-right font-bold text-hr-pink">
                {fmt(context.pricing_summary.grossPremium)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Control Panel */}
      <div className="p-5 bg-white border-t border-slate-200 space-y-5">
        <div className="flex items-center justify-between bg-slate-50 p-3 rounded border border-slate-200">
          <span className="text-micro font-bold text-slate-600 uppercase tracking-wide">
            Brokerage Commission
          </span>
          <div className="flex items-center gap-2">
            <input
              type="number"
              className="w-12 p-1 text-xs font-bold text-right border border-slate-300 rounded focus:ring-1 focus:ring-hr-indigo outline-none"
              value={context.policy_parameters.brokeragePercent}
              onChange={handleBrokerageChange}
            />
            <span className="text-xs font-bold text-slate-500">%</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={onNewQuote}
            className="px-4 py-3 text-micro font-bold uppercase tracking-widest text-slate-600 bg-white border border-slate-300 rounded hover:bg-slate-50 transition-colors"
          >
            New Quote
          </button>
          <button
            type="button"
            onClick={onFinishQuote}
            disabled={isCalculating}
            className="px-4 py-3 text-micro font-bold uppercase tracking-widest text-white bg-hr-pink rounded shadow-lg hover:bg-hr-pink-hover transition-colors flex justify-center gap-2"
          >
            {isCalculating ? 'Processing...' : 'Finish Quote'}
          </button>
        </div>
      </div>
    </div>
  );
};
